import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Search, ExternalLink, ChevronLeft, ChevronRight, Filter, X, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cacheUtils } from "@/utils/cache";
import { API, fixEncoding, SEO_CONFIG } from "@/utils/helpers";
import { 
  generateSearchResultsSchema, 
  generateBreadcrumbSchema,
  combineSchemas,
  useStructuredData
} from "@/utils/structuredData";
import Home from "./Home";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState("");
  const [facets, setFacets] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  
  // Brand detection for breadcrumb - fetched from spreadsheet database
  const [detectedBrand, setDetectedBrand] = useState(null);

  // Fetch brand from spreadsheet database when query changes
  const fetchBrandForQuery = async (query) => {
    if (!query) {
      setDetectedBrand(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/query/brand?q=${encodeURIComponent(query)}`);
      if (response.data.brand) {
        setDetectedBrand(response.data.brand);
      } else {
        setDetectedBrand(null);
      }
    } catch (err) {
      console.error('Error fetching brand for query:', err);
      setDetectedBrand(null);
    }
  };

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/autocomplete?q=${encodeURIComponent(query)}&limit=8`);
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
    }
  };

  // Debounced autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && !loading) {
        fetchSuggestions(searchQuery);
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeSuggestion >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[activeSuggestion]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (suggestion.type === 'brand') {
      navigate(`/brand/${encodeURIComponent(suggestion.text)}`);
    } else {
      setSearchQuery(suggestion.text);
      navigate(`/?query=${encodeURIComponent(suggestion.text)}&page=1`);
    }
  };

  useEffect(() => {
    const query = searchParams.get("query");
    const page = parseInt(searchParams.get("page")) || 1;
    const sort = searchParams.get("sort") || "";
    
    const filters = {};
    searchParams.forEach((value, key) => {
      if (key !== "query" && key !== "page" && key !== "sort" && key !== "filterGreaterThanEqual") {
        filters[key] = value.split(",");
      }
    });
    
    if (query) {
      setSearchQuery(query);
      setCurrentPage(page);
      setSortOption(sort);
      setSelectedFilters(filters);
      performSearch(query, page, sort, filters);
      // Fetch brand from spreadsheet database
      fetchBrandForQuery(query);
    }
  }, [searchParams]);

  const performSearch = async (query, page = 1, sort = "", filters = {}) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const filterGTE = searchParams.get("filterGreaterThanEqual");
      const cacheKey = cacheUtils.generateSearchKey({
        query, page, sort, filters, filterGreaterThanEqual: filterGTE
      });
      
      const cachedResults = cacheUtils.get(cacheKey);
      if (cachedResults) {
        setProducts(cachedResults.offers || []);
        setPagination(cachedResults.meta?.offers || null);
        
        const facetsData = cachedResults.meta?.facets || {};
        const facetsArray = Object.keys(facetsData).map(key => ({
          key: key,
          label: facetsData[key].label || facetsData[key].title || key,
          filterKey: facetsData[key].filterKey || key,
          values: (facetsData[key].values || []).map(v => ({
            ...v, slugLabel: v.slugLabel || v.value
          })),
          ranges: facetsData[key].ranges || []
        }));
        setFacets(facetsArray);
        setLoading(false);
        return;
      }
      
      const params = { query, pageSize: 100, page };
      
      if (sort === "price") {
        params.sortBy = "totalPrice";
        params.sortDirection = "asc";
      } else if (sort === "performance") {
        params.sortBy = "performanceScore";
        params.sortDirection = "desc";
      }
      
      const filterByParams = [];
      Object.keys(filters).forEach(key => {
        if (filters[key].length > 0) {
          const facet = facets.find(f => f.key === key);
          const filterKey = facet?.filterKey || key;
          filters[key].forEach(value => {
            filterByParams.push(`${filterKey}:${value}`);
          });
        }
      });
      
      if (filterByParams.length > 0) {
        params.filterBy = filterByParams;
      }
      
      if (filterGTE) {
        params.filterGreaterThanEqual = filterGTE;
      }
      
      const response = await axios.get(`${API}/search`, { params });
      const resultData = response.data;
      
      cacheUtils.set(cacheKey, resultData);
      
      setProducts(resultData.offers || []);
      setPagination(resultData.meta?.offers || null);
      
      const facetsData = resultData.meta?.facets || {};
      const facetsArray = Object.keys(facetsData).map(key => ({
        key: key,
        label: facetsData[key].label || facetsData[key].title || key,
        filterKey: facetsData[key].filterKey || key,
        values: (facetsData[key].values || []).map(v => ({
          ...v, slugLabel: v.slugLabel || v.value
        })),
        ranges: facetsData[key].ranges || []
      }));
      setFacets(facetsArray);
    } catch (error) {
      console.error("Search error:", error);
      setProducts([]);
      setPagination(null);
      setFacets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setSelectedFilters({});
    const params = { query: searchQuery, page: 1 };
    if (sortOption) params.sort = sortOption;
    setSearchParams(params);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    const params = { query: searchQuery, page: newPage };
    if (sortOption) params.sort = sortOption;
    Object.keys(selectedFilters).forEach(key => {
      if (selectedFilters[key].length > 0) {
        params[key] = selectedFilters[key].join(",");
      }
    });
    setSearchParams(params);
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (newSort) => {
    setSortOption(newSort);
    const params = { query: searchQuery, page: 1 };
    if (newSort) params.sort = newSort;
    Object.keys(selectedFilters).forEach(key => {
      if (selectedFilters[key].length > 0) {
        params[key] = selectedFilters[key].join(",");
      }
    });
    setSearchParams(params);
    setCurrentPage(1);
  };

  const handleFilterChange = (facetKey, value) => {
    const newFilters = { ...selectedFilters };
    if (!newFilters[facetKey]) newFilters[facetKey] = [];
    
    const valueIndex = newFilters[facetKey].indexOf(value);
    if (valueIndex > -1) {
      newFilters[facetKey].splice(valueIndex, 1);
      if (newFilters[facetKey].length === 0) delete newFilters[facetKey];
    } else {
      newFilters[facetKey].push(value);
    }
    
    setSelectedFilters(newFilters);
    const params = { query: searchQuery, page: 1 };
    if (sortOption) params.sort = sortOption;
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key].length > 0) {
        params[key] = newFilters[key].join(",");
      }
    });
    setSearchParams(params);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedFilters({});
    const params = { query: searchQuery, page: 1 };
    if (sortOption) params.sort = sortOption;
    setSearchParams(params);
    setCurrentPage(1);
  };

  const handleRebateFilter = () => {
    const params = { query: searchQuery, page: 1 };
    if (sortOption) params.sort = sortOption;
    Object.keys(selectedFilters).forEach(key => {
      if (selectedFilters[key].length > 0) {
        params[key] = selectedFilters[key].join(",");
      }
    });
    params.filterGreaterThanEqual = "rebatePercentage:1";
    setSearchParams(params);
    setCurrentPage(1);
  };

  const getRebateOffersCount = () => {
    const rebateFacet = facets.find(f => f.key === 'rebatePercentage');
    if (!rebateFacet || !rebateFacet.ranges) return 0;
    return rebateFacet.ranges
      .filter(range => range.from > 0 || range.to > 0)
      .reduce((sum, range) => sum + range.count, 0);
  };
  
  const rebateOffersCount = getRebateOffersCount();

  const handleOfferClick = (offer) => {
    window.open(offer.goUrl, '_blank', 'noopener,noreferrer');
  };

  const hasSearched = searchParams.get("query");

  // SEO structured data for search results - comprehensive schemas
  const query = searchParams.get("query");
  
  const breadcrumbSchema = hasSearched ? generateBreadcrumbSchema([
    { name: 'Accueil', url: SEO_CONFIG.siteUrl },
    { name: `Recherche: ${query}`, url: `${SEO_CONFIG.siteUrl}/?query=${encodeURIComponent(query)}` }
  ]) : null;
  
  const searchResultsSchema = hasSearched && products.length > 0 
    ? generateSearchResultsSchema(products, query, pagination?.available || products.length)
    : null;
  
  const structuredData = combineSchemas(breadcrumbSchema, searchResultsSchema);
  
  // Inject structured data into document head
  useStructuredData(structuredData, 'search-structured-data');

  return (
    <div className="min-h-screen">
      {hasSearched && (
        <Helmet>
          <title>{`${searchParams.get("query")} - Recherche | ${SEO_CONFIG.siteName}`}</title>
          <meta name="description" content={`Comparez ${pagination?.available || 0} offres pour "${searchParams.get("query")}". Trouvez les meilleurs prix et promotions.`} />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}

      {hasSearched && (
        <div className="search-bar-sticky">
          <div className="search-bar-container">
            <form onSubmit={handleSearch} className="search-form-compact">
              <div className="search-wrapper-compact" style={{ position: 'relative' }}>
                <Search className="search-icon-compact" />
                <Input
                  ref={inputRef}
                  data-testid="search-input"
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                    setActiveSuggestion(-1);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={handleKeyDown}
                  className="search-input-compact"
                  autoComplete="off"
                />
                <Button 
                  data-testid="search-button"
                  type="submit" 
                  disabled={loading}
                  className="search-button-compact"
                >
                  {loading ? "..." : "Rechercher"}
                </Button>
                
                {/* Autocomplete Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div ref={suggestionsRef} className="autocomplete-dropdown" data-testid="autocomplete-dropdown">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`autocomplete-item ${index === activeSuggestion ? 'active' : ''} ${suggestion.type}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        data-testid={`suggestion-${index}`}
                      >
                        {suggestion.type === 'brand' ? (
                          <>
                            <span className="suggestion-badge brand">Marque</span>
                            <span className="suggestion-text">{suggestion.text}</span>
                          </>
                        ) : (
                          <>
                            <Search className="suggestion-icon" />
                            <span className="suggestion-text">{suggestion.text}</span>
                            <span className="suggestion-brand">{suggestion.brand}</span>
                          </>
                        )}
                        <span className="suggestion-count">({suggestion.result_count})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
          
          {/* Breadcrumb Navigation */}
          <nav className="breadcrumb-nav" data-testid="breadcrumb">
            <Link to="/" className="breadcrumb-item">
              <HomeIcon className="breadcrumb-icon" />
              <span>Accueil</span>
            </Link>
            <span className="breadcrumb-separator">/</span>
            {detectedBrand && detectedBrand.trim() !== '' && detectedBrand.toLowerCase() !== 'none' && (
              <>
                <Link to={`/brand/${encodeURIComponent(detectedBrand)}`} className="breadcrumb-item">
                  {detectedBrand}
                </Link>
                <span className="breadcrumb-separator">/</span>
              </>
            )}
            <span className="breadcrumb-current">{searchParams.get("query")}</span>
          </nav>
        </div>
      )}

      {hasSearched && (
        <div className="results-section-compact">
          <div className="results-container">
            {loading ? (
              <div className="skeleton-grid">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-line short"></div>
                      <div className="skeleton-line medium"></div>
                      <div className="skeleton-line full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="results-header-section">
                  <h1 className="results-title">
                    {pagination?.available || products.length} offres "{searchParams.get("query")}" trouvées
                  </h1>
                  
                  <div className="sort-section">
                    <label htmlFor="sort-select" className="sort-label">Trier par:</label>
                    <select 
                      id="sort-select"
                      data-testid="sort-select"
                      value={sortOption}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="sort-select"
                    >
                      <option value="">Pertinence</option>
                      <option value="price">Prix croissant</option>
                      <option value="performance">Meilleures ventes</option>
                    </select>
                  </div>

                  {facets.length > 0 && (
                    <button
                      onClick={() => setShowMobileFilters(true)}
                      className="mobile-filter-button"
                      data-testid="mobile-filter-button"
                    >
                      <Filter className="w-4 h-4" />
                      Filtres
                      {Object.keys(selectedFilters).length > 0 && (
                        <span className="filter-count">{Object.keys(selectedFilters).length}</span>
                      )}
                    </button>
                  )}
                </div>

                {showMobileFilters && (
                  <div className="mobile-filters-overlay" onClick={() => setShowMobileFilters(false)}>
                    <div className="mobile-filters-panel" onClick={(e) => e.stopPropagation()}>
                      <div className="mobile-filters-header">
                        <h2>Filtres</h2>
                        <button onClick={() => setShowMobileFilters(false)} className="close-filters-btn" data-testid="close-filters-btn">
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="mobile-filters-content">
                        {rebateOffersCount > 0 && (
                          <div className="rebate-offers-section">
                            <Button
                              onClick={() => { handleRebateFilter(); setShowMobileFilters(false); }}
                              className="rebate-offers-btn"
                              data-testid="rebate-offers-btn"
                            >
                              🎯 {rebateOffersCount} offres avec remises
                            </Button>
                          </div>
                        )}

                        <div className="mobile-filters-actions">
                          {Object.keys(selectedFilters).length > 0 && (
                            <button 
                              onClick={() => { clearFilters(); setShowMobileFilters(false); }}
                              className="clear-filters-btn"
                              data-testid="clear-filters-btn"
                            >
                              Effacer tout
                            </button>
                          )}
                        </div>

                        {facets.map((facet) => (
                          facet.values && facet.values.length > 0 && (
                            <div key={facet.key} className="filter-group">
                              <h3 className="filter-title">{facet.label || facet.key}</h3>
                              <div className="filter-options">
                                {facet.values.slice(0, 10).map((value) => (
                                  <label key={value.value} className="filter-option">
                                    <input
                                      type="checkbox"
                                      checked={selectedFilters[facet.key]?.includes(value.value) || false}
                                      onChange={() => handleFilterChange(facet.key, value.value)}
                                      className="filter-checkbox"
                                    />
                                    <span className="filter-label-text">{value.label} ({value.count})</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )
                        ))}
                      </div>

                      <div className="mobile-filters-footer">
                        <Button onClick={() => setShowMobileFilters(false)} className="apply-filters-btn" data-testid="apply-filters-btn">
                          Voir les résultats
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="content-layout">
                  {facets.length > 0 && (
                    <aside className="filters-sidebar">
                      {rebateOffersCount > 0 && (
                        <div className="rebate-offers-section">
                          <Button onClick={handleRebateFilter} className="rebate-offers-btn" data-testid="rebate-offers-btn">
                            🎯 {rebateOffersCount} offres avec remises
                          </Button>
                        </div>
                      )}
                      
                      <div className="filters-header">
                        <h2>Filtres</h2>
                        {Object.keys(selectedFilters).length > 0 && (
                          <button onClick={clearFilters} className="clear-filters-btn" data-testid="clear-filters-btn">
                            Effacer tout
                          </button>
                        )}
                      </div>
                      
                      {facets.map((facet) => (
                        facet.values && facet.values.length > 0 && (
                          <div key={facet.key} className="filter-group">
                            <h3 className="filter-title">{facet.label || facet.key}</h3>
                            <div className="filter-options">
                              {facet.values.slice(0, 10).map((value) => (
                                <label key={value.value} className="filter-option">
                                  <input
                                    type="checkbox"
                                    checked={selectedFilters[facet.key]?.includes(value.value) || false}
                                    onChange={() => handleFilterChange(facet.key, value.value)}
                                    className="filter-checkbox"
                                  />
                                  <span className="filter-label-text">{value.label} ({value.count})</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </aside>
                  )}
                  
                  <div className="products-grid">
                    {products.map((offer) => (
                      <Card key={offer.offerId} className="product-card" data-testid="product-card" onClick={() => handleOfferClick(offer)}>
                        <div className="product-image-container">
                          {offer.images?.[0]?.url && (
                            <img src={offer.images[0].url} alt={offer.title} className="product-image" loading="lazy" />
                          )}
                          <div className="price-overlay">
                            <span className="price-value">{offer.price.toFixed(2)}€</span>
                          </div>
                          {offer.rebatePercentage > 0 && (
                            <Badge className="rebate-badge">-{offer.rebatePercentage}%</Badge>
                          )}
                          {offer.flag?.saleEvent && !offer.rebatePercentage && (
                            <Badge className="sale-badge">Promo</Badge>
                          )}
                        </div>
                        
                        <div className="product-content">
                          <div className="merchant-row">
                            <span className="merchant-name">{fixEncoding(offer.merchant?.name)}</span>
                            {offer.deliveryCost === 0 && (
                              <span className="delivery-icon" title="Livraison gratuite">🚚</span>
                            )}
                          </div>
                          
                          <h3 className="product-title" title={fixEncoding(offer.title)}>{fixEncoding(offer.title)}</h3>
                          
                          <div className="product-actions">
                            <Button
                              data-testid="view-offer-button"
                              onClick={(e) => { e.stopPropagation(); handleOfferClick(offer); }}
                              className="offer-button"
                            >
                              <span>Voir l'offre</span>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            
                            {offer.code?.ean && (
                              <Button
                                data-testid="compare-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/compare/${offer.code.ean}?query=${encodeURIComponent(searchQuery)}`);
                                }}
                                className="compare-button"
                                variant="outline"
                              >
                                <span>Comparer</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                {pagination && pagination.totalPages > 1 && (
                  <div className="pagination">
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                      data-testid="prev-page-button"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Précédent
                    </Button>

                    <div className="pagination-pages">
                      {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                        else pageNum = currentPage - 2 + i;
                        
                        return (
                          <Button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            data-testid={`page-${pageNum}-button`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="pagination-button"
                      data-testid="next-page-button"
                    >
                      Suivant
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-results">
                <Search className="no-results-icon" />
                <h3>Aucune offre trouvée</h3>
                <p>Essayez une autre recherche</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!hasSearched && <Home />}
    </div>
  );
};

export default SearchPage;
